import { Plane, BedDouble, Car, ArrowLeftRight, Sparkles, Package as PackageIcon } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useEffect, useState } from 'react'
import JsonLd from '../components/JsonLd'
import { publicUrl } from '../utils/publicUrl'

//

export default function Home() {
  const { franchiseData, loading } = useFranchiseData()

  const [brandIndex, setBrandIndex] = useState(0)
  const heroImageSrc = publicUrl('Northern%20Lights.png')

  // Travel experiences carousel data
  const travelExperiences = [
    {
      title: 'IATA',
      subtitle: 'Accredited',
      description: 'At Indo Jet Travel, we are proud to be IATA accredited, which not only underscores our commitment to maintaining the highest standards in the travel industry but also positions us within a globally recognized network of travel professionals. This accreditation grants us access to various booking aggregator systems, ensuring that we can provide the most comprehensive and competitive travel solutions for our clients.',
      image: publicUrl('world-book-day-concept-businessman-reading-book-plane-generative-ai-605x605.jpg'),
      fallbackImage: publicUrl('city.jpg')
    },
    {
      title: 'Global',
      subtitle: 'Presence',
      description: 'Our global presence, marked by our office in Sydney, Australia, amplifies our reach and capabilities. While we have a strong foothold in the South African market, our global operations mean that even when options in South Africa are exhausted, we can tap into international markets to provide additional choices. This global approach ensures that we always have a plethora of options to offer, making us a versatile and reliable travel partner.',
      image: publicUrl('image001-1-605x605.jpg'),
      fallbackImage: publicUrl('beach.jpg')
    },
    {
      title: 'Experienced',
      subtitle: 'Experts',
      description: 'Experienced Travel Experts can provide a higher level of customer service, addressing unique needs and concerns.',
      image: publicUrl('Untitled-design-768x768.png'),
      fallbackImage: publicUrl('tours.jpg')
    },
    {
      title: 'Executive',
      subtitle: 'Travel',
      description: 'Indo Jet Travel takes pride in its executive travel services, designed exclusively for the discerning needs of our corporate clients. We understand that executive travel often requires a higher degree of precision, luxury, and efficiency. Our services in this realm encompass everything from premium flight bookings and luxury accommodation reservations to personalized itineraries and exclusive experiences. With a keen eye for detail and a commitment to excellence, we ensure that our executive travelers receive the best in class, every time.',
      image: publicUrl('Indojet-Travel-2-768x768.png'),
      fallbackImage: publicUrl('city.jpg')
    }
  ]

  // Brand logos carousel data
  const brandLogos = [
    publicUrl('discovery-removebg-preview.png'),
    publicUrl('iVeri-removebg-preview.png'),
    publicUrl('Visa_Inc._logo.svg-removebg-preview.png'),
    publicUrl('MasterCard_Logo.svg-removebg-preview.png'),
    publicUrl('American-Express-Logo-2018-present-removebg-preview.png'),
    publicUrl('IATAlogo.svg-removebg-preview.png'),
  ]

  // Auto-advance brand carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setBrandIndex((prev) => (prev + 1) % brandLogos.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [brandLogos.length])

  if (loading || !franchiseData) {
    return (
      <div className="bg-indojet-light min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }


  return (
    <div className="bg-indojet-light">
      <Helmet>
         <title>Indo Jet Travel | Corporate & Leisure Travel Solutions</title>
         <link rel="preload" as="image" href={heroImageSrc} />
         <meta
           name="description"
           content="For over 50 years, Indo Jet Travel has delivered seamless travel solutions globally. Backed by Tourvest Holdings, we make travel simple, safe, and cost-effective."
         />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/' : '/'}
        />
        <meta property="og:type" content="website" />
         <meta property="og:title" content="Indo Jet Travel | Corporate & Leisure Travel Solutions" />
         <meta
           property="og:description"
           content="For over 50 years, Indo Jet Travel has delivered seamless travel solutions globally. Backed by Tourvest Holdings."
         />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Home',
          url: typeof window !== 'undefined' ? window.location.href : '/',
           description:
             "For over 50 years, Indo Jet Travel has delivered seamless travel solutions globally. Backed by Tourvest Holdings.",
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
              },
            ],
          },
        }}
      />
      {/* Hero section */}
      {/* Height compensates for the global html { zoom: 0.9 } so the hero fills the actual viewport. */}
      <div className="relative h-[calc(100vh/0.9)] min-h-[calc(600px/0.9)] w-full flex items-center justify-center overflow-hidden">
        {/* Image Background */}
        <img
          src={heroImageSrc}
          alt="Indo Jet Travel hero"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        
        {/* Main Content Container */}
        <div className="relative z-10 w-full h-full max-w-7xl mx-auto px-6 lg:px-8 pt-24 sm:pt-28">
          <div className="flex flex-col justify-center items-start h-full">
            
            {/* Large Text */}
            <div className="flex flex-col justify-center text-left max-w-4xl">
              <h1 className="font-display font-light text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-8">
                <span className="block">Change</span>
                <span className="block">the Way</span>
                <span className="block">You See</span>
                <span className="block">the World</span>
              </h1>
              <div className="flex items-center justify-start gap-3">
                <Link to="/packages" className="bg-[#182c78] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#182c78]/90 transition-all duration-300 flex items-center gap-2 text-sm">
                  <span>Explore</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Section - Snapshot */}
      <div id="about" className="bg-indojet-light py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div className="flex items-center gap-4">
              <span className="hidden sm:block h-px w-16 bg-[#182c78]" />
              <p className="tracking-[0.6em] text-sm font-semibold text-[#182c78]">
                ABOUT
              </p>
            </div>
            <p>
              Indo Jet Travel Sandton has a storied legacy of global operations, spanning back to its
              establishment in 1967. In 1990, when Sharon Seskin assumed ownership of the business, the agency
              embarked on a transformative journey that significantly expanded its international reach.
            </p>
            <p>
              Fast forward to 2011, under her visionary leadership, Indo Jet Travel Sandton proudly opened an
              office in Sydney, Australia, marking a pivotal moment in the company’s history. This strategic move
              has propelled us into a more globally connected era, providing us with expanded access to diverse and
              dynamic international markets.
            </p>
            <p>
              At Indo Jet Travel Sandton, our commitment to delivering exceptional travel solutions resonates with
              the comprehensive scope of services you are seeking. We recognize that travel needs span an array of
              dimensions, and we are poised to address each one with dedication and expertise.
            </p>

            <div className="pt-4">
              <img
                src={publicUrl('discovery-159x42.png')}
                alt="Discovery logo"
                className="h-10 w-auto"
              />
            </div>

            <p className="text-sm font-semibold text-gray-900 tracking-wide">
              DISCOVERING THE LEGACY:{' '}
              <span className="font-normal normal-case text-gray-700">
                Indo Jet Travel, one of Discovery’s longest Travel Providers.
              </span>
            </p>

            <Link
              to="/about"
              className="inline-flex px-6 py-3 rounded-full bg-[#182c78] text-white font-semibold text-sm hover:bg-[#182c78]/90 transition-colors"
            >
              Read more
            </Link>
          </div>
        </div>
      </div>

      {/* Parallax banner – scrolls under the next section */}
      <div className="relative z-0 min-h-[70vh]">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url("${encodeURI(publicUrl('Woman with pink suitcase at airport.png'))}")`,
          }}
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 min-h-[70vh] flex items-center justify-center px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#182c78] leading-tight not-italic whitespace-normal md:whitespace-nowrap max-w-4xl text-center">
            Explore Beyond Boundaries: Your Journey, Our Expertise!
          </h2>
        </div>
      </div>

      {/* Travel Experiences – four-column layout */}
      <div className="relative z-10 py-16 sm:py-20 bg-slate-100">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {travelExperiences.map((experience, index) => (
              <div key={index} className="flex flex-col bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = experience.fallbackImage
                    }}
                  />
                </div>
                <div className="p-5 flex-1">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {experience.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Parallax banner (image only) – scrolls under next section */}
      <div className="relative z-0 min-h-[70vh]">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url("${encodeURI(publicUrl('WhatsApp Image 2026-02-20 at 12.05.21 PM.jpeg'))}")`,
          }}
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Travel Essentials Section */}
      <div className="relative z-10 py-16 sm:py-20 bg-indojet-light">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Travel Essentials
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              End-to-end support for every part of your journey, from flights and stays to on-the-ground mobility and bespoke experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <div className="space-y-3">
              <Plane className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Flights</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Intricate and multi-segment itineraries handled with precision so every connection feels seamless.
              </p>
            </div>

            <div className="space-y-3">
              <BedDouble className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Accommodation</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                A global network of handpicked stays tailored to your preferences for comfort and memorable experiences.
              </p>
            </div>

            <div className="space-y-3">
              <Car className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Car Hire</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Vehicles matched to your requirements, ensuring freedom of movement and a smooth arrival-to-departure journey.
              </p>
            </div>

            <div className="space-y-3">
              <ArrowLeftRight className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Transfers</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Safe, efficient ground transport with a focus on comfort so you can relax and enjoy the ride.
              </p>
            </div>

            <div className="space-y-3">
              <Sparkles className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Concierge Services</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Special requests, bespoke experiences, and meticulous attention to detail to elevate every journey.
              </p>
            </div>

            <div className="space-y-3">
              <PackageIcon className="w-8 h-8 text-[#182c78]" />
              <h3 className="text-lg font-bold text-gray-900">Packages</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Thoughtfully tailored travel packages designed to meet unique needs and deliver unforgettable trips.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Banner Carousel */}
      <div className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-8">
            <p className="text-gray-600 text-sm sm:text-base font-medium">Trusted brands we work with</p>
          </div>
          <div className="relative bg-white p-6 sm:p-8 overflow-hidden">
            <div className="flex items-center justify-center gap-8 sm:gap-12">
              {Array.from({ length: Math.min(6, brandLogos.length) }).map((_, i) => {
                const logo = brandLogos[(brandIndex + i) % brandLogos.length]
                const isDiscovery = logo === publicUrl('discovery-removebg-preview.png')
                return (
                  <div key={`${logo}-${i}`} className="flex-shrink-0 flex items-center justify-center">
                    <img
                      src={logo}
                      alt="Partner logo"
                      className={`object-contain ${isDiscovery ? 'h-16 sm:h-28' : 'h-8 sm:h-12'}`}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
